import api from '../api';
import { taskPollingService } from './taskPollingService';
import type { AnalysisResult, GenerationResponse } from './types';

// Style Analysis
const analyzeStyle = async (imageFile: File): Promise<AnalysisResult> => {
    const formData = new FormData();
    formData.append('image', imageFile);

    const response = await api.post('/ai/analyze-style', formData, {
        headers: {
            'Content-Type': 'multipart/form-data',
        },
    });
    // The backend currently returns { message, task_id } for async analysis
    // But we might want to poll for the result if the UI expects it immediately.
    // For now, let's assume the UI will handle the async nature or we poll here.
    // If the UI expects the analysis result immediately (like the current geminiService),
    // we must poll.

    const data = response.data as GenerationResponse;
    const completedTask = await taskPollingService.pollTask(data.taskId);

    // The analysis result is stored in metadata.analysis_result or similar
    // TaskResource returns 'analysis_results' or 'metadata'
    // Let's check TaskResource structure. It has 'analysis_results'.

    return {
        analysis: completedTask.analysisResults || completedTask.metadata?.analysisResult,
        usage: completedTask.tokenUsage
    };
};

// Optimize Prompt for Editing
const optimizePrompt = async (prompt: string, baseImage: File | string, referenceImages: File[] = []): Promise<string> => {
    const formData = new FormData();
    formData.append('prompt', prompt);

    if (baseImage instanceof File) {
        formData.append('base_image', baseImage);
    } else {
        formData.append('base_image', baseImage); // Send URL as string
    }

    referenceImages.forEach((file, index) => {
        formData.append(`reference_images[${index}]`, file);
    });

    try {
        const response = await api.post('/ai/optimize-prompt', formData, {
            headers: {
                'Content-Type': 'multipart/form-data',
            },
        });
        return (response.data as any).data.optimizedPrompt;
    } catch (error) {
        console.error('Failed to optimize prompt:', error);
        return prompt; // Return original prompt on failure
    }
};

// Analyze Style from URL
const analyzeUrlForStyle = async (url: string): Promise<string> => {
    // Assuming backend supports image_url for analysis
    const response = await api.post('/ai/analyze-style', { url });
    const data = response.data as GenerationResponse;
    const completedTask = await taskPollingService.pollTask(data.taskId);
    return completedTask.analysisResults?.stylePrompt || completedTask.analysisResults?.description || "Estilo analisado";
};

export const analysisService = { analyzeStyle, optimizePrompt, analyzeUrlForStyle };
